import { ReactNode, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  DhruvantaAppShell,
  type AppShellNavGroup,
  type AppShellNavItem,
  type AppShellTheme
} from '@dhruvanta/ui/react'

/**
 * KAN-2035 — the ONE chrome every Dhruvanta Retail ROLE portal (seller / operations /
 * admin) renders inside, per the approved redesign mock (KAN-2034).
 *
 * It is a thin, opinionated wrapper over the shared `<DhruvantaAppShell>` from
 * @dhruvanta/ui:
 *   * `product="retail"` + `syncDocument` — activates the warm commerce amber
 *     (`[data-product="retail"]`) and hoists data-theme/data-product to <html>, which
 *     is what the compound `[data-theme][data-product]` token rules need;
 *   * `railLayout="beside"` + the STRUCTURED `nav` prop (KAN-2016) — the shell renders
 *     the grouped left rail itself and folds it into its own accessible mobile drawer
 *     below 880px. No portal hand-rolls a navbar/hamburger any more;
 *   * the mock's brand lockup in the rail and the glass topbar (search + actions).
 *
 * The STOREFRONT deliberately does NOT use this (it is a shop, not a console) and the
 * DELIVERY portal is mobile-first single-column — see the mock's design notes.
 *
 * Router-agnostic by construction upstream, so routing is wired HERE: {@link useRetailNav}
 * turns plain route descriptors into `AppShellNavItem`s that navigate with react-router
 * and own their own `active` flag.
 */

export interface RetailNavEntry {
  /** Router path this entry navigates to (relative or absolute, as the portal uses). */
  to: string
  label: string
  /** Optional leading icon (rendered aria-hidden by the shell). */
  icon?: ReactNode
  /** Optional trailing badge — a count or short status. */
  badge?: ReactNode
  /** Match the path EXACTLY rather than as a prefix (for index/root routes). */
  end?: boolean
}

export interface RetailNavSection {
  id: string
  /** Group heading in the rail. Omit for a flat, heading-less list. */
  label?: string
  items: RetailNavEntry[]
}

/** Strip trailing slashes so `/orders` and `/orders/` compare equal. */
const normalise = (path: string) => path.replace(/\/+$/, '') || '/'

/**
 * Build the shell's structured nav from plain route descriptors: resolves `active`
 * against the current location and wires each item to react-router.
 *
 * Items render as real `<a href>` links (keyboard + middle-click + "open in new tab"
 * all keep working). The shell passes the click event through to `onSelect`, so the
 * default full-page navigation is prevented and the SPA router takes over; if that
 * ever stops holding, the anchor's href still lands on the same route — it just costs
 * a reload. Never a dead link.
 */
export function useRetailNav(sections: RetailNavSection[]): AppShellNavGroup[] {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = normalise(location.pathname)

  return useMemo(
    () =>
      sections.map(section => ({
        id: section.id,
        label: section.label,
        items: section.items.map<AppShellNavItem>(entry => {
          const target = normalise(entry.to)
          const active = entry.end
            ? pathname === target
            : pathname === target || pathname.startsWith(`${target}/`)

          return {
            id: entry.to,
            label: entry.label,
            icon: entry.icon,
            badge: entry.badge,
            active,
            href: entry.to,
            onSelect: ((event?: { preventDefault?: () => void; metaKey?: boolean; ctrlKey?: boolean }) => {
              // Let the browser handle modified clicks (new tab / new window).
              if (event?.metaKey || event?.ctrlKey) return
              event?.preventDefault?.()
              navigate(entry.to)
            }) as () => void
          }
        })
      })),
    [sections, pathname, navigate]
  )
}

export interface RetailAppShellProps {
  /** Role name shown under "Dhruvanta Retail" in the rail (e.g. "Seller"). */
  role: string
  /** Grouped rail navigation, already resolved by {@link useRetailNav}. */
  nav: AppShellNavGroup[]
  /** Accessible name for the rail's <nav> landmark. */
  navAriaLabel?: string
  /** Placeholder for the topbar's search affordance. Omit to hide it. */
  searchPlaceholder?: string
  /** Topbar trailing slot — notifications, chips, avatar, logout. */
  topbarEnd?: ReactNode
  /**
   * Colour scheme. Pinned to "light" by default rather than "system": the retail dark
   * palette is fully wired in tokens (see src/styles/tokens.css) and proven by the
   * delivery console, but a handful of legacy portal stylesheets still hard-code light
   * surfaces. Flip this to "system" once those are token-driven (tracked separately).
   */
  theme?: AppShellTheme
  children: ReactNode
}

export function RetailAppShell({
  role,
  nav,
  navAriaLabel,
  searchPlaceholder,
  topbarEnd,
  theme = 'light',
  children
}: RetailAppShellProps) {
  const sidebar = (
    <div className="rt-rail-brand">
      <span className="rt-mark" aria-hidden="true">
        D
      </span>
      <span className="rt-rail-brand__name">
        <strong>Dhruvanta Retail</strong>
        <small>{role}</small>
      </span>
    </div>
  )

  const centerBlocks = searchPlaceholder
    ? [
        <span className="rt-search" key="search">
          {searchPlaceholder}
        </span>
      ]
    : []

  const endBlocks = topbarEnd
    ? [
        <span className="rt-topbar__end" key="actions">
          {topbarEnd}
        </span>
      ]
    : []

  return (
    <DhruvantaAppShell
      className="rt-appshell"
      variant="app"
      product="retail"
      theme={theme}
      syncDocument
      railLayout="beside"
      sidebar={sidebar}
      nav={nav}
      navAriaLabel={navAriaLabel ?? `${role} navigation`}
      header={{
        regions: { start: [], center: centerBlocks, end: endBlocks },
        elevated: true
      }}
      footer={false}
    >
      {children}
    </DhruvantaAppShell>
  )
}

export interface RetailPageHeaderProps {
  /** Small uppercase accent line above the title (the mock's `.eyebrow`). */
  eyebrow?: string
  title: string
  subtitle?: ReactNode
  /** Trailing actions (buttons / chips). */
  actions?: ReactNode
}

/**
 * The mock's page header: eyebrow + title (+ subtitle) with trailing actions.
 * The title is the routed page's single `<h1>`.
 */
export function RetailPageHeader({ eyebrow, title, subtitle, actions }: RetailPageHeaderProps) {
  return (
    <div className="rt-page-head">
      <div className="rt-page-head__titles">
        {eyebrow ? <p className="rt-eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {subtitle ? <p className="rt-page-head__sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="rt-page-head__actions">{actions}</div> : null}
    </div>
  )
}
